
require("./../../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import qs from 'qs';
import { useEffect } from 'react';

import { BrandStoryLanding } from 'brand/components/BrandStoryLanding';
import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { cmsRequest } from 'server/services/cms-service/cmsRequest';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { formatStringLowercaseWithDashes } from 'shared/utils/formatStringLowercaseWithDashes';
import { getThemeBackgroundColorFromSpaceArticle } from 'shared/utils/getThemeBackgroundColorFromSpaceArticle';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { ISpaceArticleSectionFeed } from 'shared/types/SpaceArticleSection';
import { navigationThemes } from 'styles/theme/navigation';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { setToggleCurrentTheme } from 'store/slice/pageLayoutSlice';
import { startCase } from 'shared/utils/startCase';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { allPromiseSettled } from 'shared/utils/allPromiseSettled';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

interface IBrandProps extends IProductLandingPage {
  article?: ISpaceArticleSectionFeed;
  backgroundColor?: string | null;
  collectionDisplayName?: string;
  isInApp?: boolean;
  metaImage?: string | null;
  showCollection?: string;
  isCrawler: boolean;
}

const Brand = ({
  article,
  backgroundColor,
  breadcrumbs,
  browseKey,
  browseValue,
  collectionDisplayName,
  constructorResponse,
  displayName,
  isInApp = false,
  metaImage,
  globalPricingId,
  showCollection,
  isCrawler,
}: IBrandProps) => {
  const storeDispatch = useStoreDispatch();
  const enableBrandStory =
    useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_BRAND_STORY) && article?.sections?.length > 0;

  const {
    name = displayName,
    description = '',
    headerAsset = { url: '' },
    metadataDescription = null,
    titleTag = '',
  } = breadcrumbs || {};

  const isMounted = useIsMounted();

  useEffect(() => {
    if (!enableBrandStory && isMounted()) {
      storeDispatch(setToggleCurrentTheme(navigationThemes.light));
    }
  }, []);

  return enableBrandStory ? (
    <BrandStoryLanding
      article={article}
      backgroundColor={backgroundColor}
      brandName={displayName}
      browseKey={browseKey}
      browseValue={browseValue}
      collectionDisplayName={collectionDisplayName}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      isInApp={isInApp}
      metadataDescription={metadataDescription}
      metaTitle={titleTag || `${displayName} `}
      showCollection={showCollection}
      isCrawler={isCrawler}
    />
  ) : (
    <PLPLandingPageComponent
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaImage={metaImage || headerAsset?.url}
      metaTitle={titleTag}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { slug }: { slug: string } = query;
  const q = qs.parse(query);
  const { showCollection = 'false', app } = q;
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.BRAND}/${slug}`,
  });
  const isCrawler = isSearchCrawler(req?.headers);

  // Check if collection for this slug exists, if not browse for brand
  const brandName: string = startCase(slug, {
    ignoreSpecialDisplay: true,
  }).toLowerCase();
  let collectionResponse;
  let brandResponse;

  try {
    [collectionResponse, brandResponse] = await allPromiseSettled([
      getBrowseResults({
        req,
        res,
        query,
        resolvedUrl,
        filterKey: 'collection_id',
        filterValue: slug,
      }),
      getBrowseResults({
        req,
        res,
        query,
        resolvedUrl,
        filterKey: 'brand',
        filterValue: brandName,
      }),
    ]);
  } catch (error) {
    bugsnag(error, { context: 'Fetching collection and brand ' });
    return {
      notFound: true,
    };
  }

  const invalidCollectionSearch =
    collectionResponse.status === 'rejected' ||
    collectionResponse.value?.response?.total_num_results === 0;
  const invalidBrandSearch =
    brandResponse.status === 'rejected' || brandResponse.value?.response?.total_num_results === 0;

  if ((!invalidCollectionSearch && invalidBrandSearch) || invalidBrandSearch) {
    return {
      notFound: true,
    };
  }

  if (!invalidCollectionSearch) {
    // eslint-disable-next-line camelcase
    const {
      response: {
        collection: { display_name: displayName, id, data },
      },
    }: IConstructorBrowseResponse = collectionResponse.value;

    if (data?.published_cms_article_slug) {
      try {
        const cmsRes = cmsRequest(req).get(
          `api/v1/cms/cms_articles/${data.published_cms_article_slug}`,
        );
        const article: ISpaceArticleSectionFeed = await cmsRes.json();

        const backgroundColor: string =
          article?.sections?.length > 0
            ? getThemeBackgroundColorFromSpaceArticle(article?.sections?.[0])
            : null;
        return {
          props: {
            ...(await setServerSideTranslations({ locale })),
            article,
            backgroundColor,
            breadcrumbs,
            browseKey: 'collection_id',
            browseValue: id,
            collectionDisplayName: displayName,
            constructorResponse: collectionResponse.value,
            displayName: startCase(slug),
            globalPricingId,
            isInApp: app === 'y',
            metaImage: data.header_asset?.type === 'picture' ? data.header_asset?.url : null,
            showCollection,
            slug,
            isCrawler,
          },
        };
      } catch (error) {
        bugsnag(error, { context: 'Failed to fetch CMS Space for Brand Page' });
      }
    }
  }

  let brandDisplayName: string = startCase(slug);
  const normalizedBrandName = normalizeValue(slug);

  // BUY3-1381: need to redirect special character brand names to normalized value
  // as Google is still crawling V1 GOAT
  if (normalizedBrandName !== brandName) {
    return {
      redirect: {
        destination: getRedirectionPath(
          `/brand/${formatStringLowercaseWithDashes(normalizedBrandName)}`,
          locale,
        ),
        permanent: true,
      },
    };
  }

  const brandFacet = brandResponse.value?.response?.facets?.find(
    (facet) => facet?.name === 'brand',
  );
  if (brandFacet) {
    brandDisplayName = brandFacet?.options[0]?.display_name;
  }

  return {
    props: {
      ...(await setServerSideTranslations({ locale })),
      breadcrumbs,
      browseKey: 'brand',
      browseValue: brandName,
      constructorResponse: brandResponse.value,
      displayName: brandDisplayName,
      globalPricingId,
      showCollection,
      slug,
      isCrawler,
    },
  };
};

export default Brand;
